"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Card, CardBody, Row, Col, Badge } from "reactstrap"
import { Btn } from "../../../AbstractElements"
import type { ExhibitionPricing } from "./types"
import { ExhibitionPricingService } from "../../../Service/ExhibitionRelatedService"
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaIdBadge,
  FaPrint,
  FaUsers,
  FaCar,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaDollarSign,
} from "react-icons/fa"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  exhibitionId: number
  onAdd: () => void
  onEdit: (item: ExhibitionPricing) => void
  refresh: boolean
}

const ExhibitionPricingTab: React.FC<Props> = ({ exhibitionId, onAdd, onEdit, refresh }) => {
  const [data, setData] = useState<ExhibitionPricing | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await ExhibitionPricingService.get(exhibitionId)
      console.log("Pricing data:", response) // Debug log
      setData(response)
    } catch (err: any) {
      console.error("Failed to fetch pricing:", err)
      if (err.response?.status === 404) {
        // No pricing exists yet
        setData(null)
      } else {
        toast.error("Failed to fetch pricing")
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [exhibitionId, refresh])

  const handleDelete = async () => {
    if (!data) return

    const result = await Swal.fire({
      title: "Delete Pricing Configuration?",
      text: "This action will permanently remove all pricing information for this exhibition. This cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete It!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    })

    if (result.isConfirmed) {
      try {
        await ExhibitionPricingService.delete(data.id)

        // Success alert
        await Swal.fire({
          title: "Deleted!",
          text: "Pricing configuration has been successfully deleted.",
          icon: "success",
          confirmButtonColor: "#198754",
          timer: 2000,
          timerProgressBar: true,
        })

        setData(null)
      } catch (error) {
        // Error alert
        await Swal.fire({
          title: "Error!",
          text: "Failed to delete pricing configuration. Please try again.",
          icon: "error",
          confirmButtonColor: "#dc3545",
        })
      }
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
            <FaDollarSign className="text-primary" size={20} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold">Exhibition Pricing</h5>
            <small className="text-muted">Manage pricing structure for this exhibition</small>
          </div>
        </div>
        {data ? (
          <div className="d-flex gap-2">
            <Btn color="info" size="sm" onClick={() => onEdit(data)} className="d-flex align-items-center">
              <FaEdit className="me-1" size={12} /> Edit Pricing
            </Btn>
            <Btn color="danger" size="sm" onClick={handleDelete} className="d-flex align-items-center">
              <FaTrash className="me-1" size={12} /> Delete
            </Btn>
          </div>
        ) : (
          <Btn color="primary" size="sm" onClick={onAdd} className="d-flex align-items-center">
            <FaPlus className="me-1" size={12} /> Add Pricing
          </Btn>
        )}
      </div>

      {data ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <Row className="g-4">
              {/* Badge Fee */}
              <Col lg="3" md="6" sm="6" className="text-center">
                <div
                  className="bg-success bg-opacity-10 rounded-circle p-3 mx-auto mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaIdBadge className="text-success" size={24} />
                </div>
                <h6 className="text-muted mb-1 fw-medium">Badge Fee</h6>
                <h4 className="text-success fw-bold mb-1">
                  {data.currency} {data.badge_fee}
                </h4>
                <small className="text-muted">Per badge issued</small>
              </Col>

              {/* Badge Reprint Price */}
              <Col lg="3" md="6" sm="6" className="text-center">
                <div
                  className="bg-warning bg-opacity-10 rounded-circle p-3 mx-auto mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaPrint className="text-warning" size={24} />
                </div>
                <h6 className="text-muted mb-1 fw-medium">Badge Reprint</h6>
                <h4 className="text-warning fw-bold mb-1">
                  {data.currency} {data.badge_reprint_price}
                </h4>
                <small className="text-muted">Per reprint request</small>
              </Col>

              {/* Visitor Entry Fee */}
              <Col lg="3" md="6" sm="6" className="text-center">
                <div
                  className="bg-info bg-opacity-10 rounded-circle p-3 mx-auto mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaUsers className="text-info" size={24} />
                </div>
                <h6 className="text-muted mb-1 fw-medium">Visitor Entry</h6>
                <h4 className="text-info fw-bold mb-1">
                  {data.currency} {data.visitor_entry_fee}
                </h4>
                <small className="text-muted">Per visitor entry</small>
              </Col>

              {/* Parking Fee */}
              <Col lg="3" md="6" sm="6" className="text-center">
                <div
                  className="bg-danger bg-opacity-10 rounded-circle p-3 mx-auto mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <FaCar className="text-danger" size={24} />
                </div>
                <h6 className="text-muted mb-1 fw-medium">Parking Fee</h6>
                <h4 className="text-danger fw-bold mb-1">
                  {data.currency} {data.parking_fee_per_minute}
                </h4>
                <small className="text-muted">Per minute</small>
              </Col>
            </Row>

            {/* Divider */}
            <hr className="my-4" />

            {/* Additional Info */}
            <Row className="align-items-center">
              <Col md="6">
                <div className="d-flex align-items-center">
                  <div className="bg-secondary bg-opacity-10 rounded-circle p-2 me-3">
                    <FaMoneyBillWave className="text-secondary" size={16} />
                  </div>
                  <div>
                    <h6 className="mb-1 fw-semibold">Currency</h6>
                    <Badge color="secondary" pill className="px-3 py-2">
                      {data.currency}
                    </Badge>
                  </div>
                </div>
              </Col>
              {data.created_at && (
                <Col md="6">
                  <div className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <FaCalendarAlt className="text-primary" size={16} />
                    </div>
                    <div>
                      <h6 className="mb-1 fw-semibold">Created Date</h6>
                      <small className="text-muted fw-medium">
                        {new Date(data.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </small>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>
      ) : (
        <div className="text-center py-5">
          <Card className="border-0 shadow-sm">
            <CardBody className="p-5">
              <div className="bg-light rounded-circle p-4 mx-auto mb-4" style={{ width: "100px", height: "100px" }}>
                <FaDollarSign className="text-muted" size={32} />
              </div>
              <h4 className="text-muted mb-3">No Pricing Configuration</h4>
              <p className="text-muted mb-4">
                This exhibition doesn't have pricing configured yet. Set up pricing to manage fees for badges, visitors,
                and parking.
              </p>
              <Btn color="primary" size="lg" onClick={onAdd} className="d-flex align-items-center mx-auto">
                <FaPlus className="me-2" size={14} />
                Configure Pricing
              </Btn>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  )
}

export default ExhibitionPricingTab
